import { useTranslation } from "react-i18next";
import Button from "@/Components/Button/Button";
import { Controller, useForm } from "react-hook-form";
import Modal, { ModalProps } from "../Modal/Modal";
import ModalTitle from "../Modal/ModalTitle";
import usePosOrderCustomDiscount from "@/shared/atoms/usePosOrderCustomDiscount";
import { useSetModal } from "@/shared/atoms/useModal";
import { CustomDiscountType } from "@/shared/utils/enum";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import TextfieldWithNumberBoard from "../Input/TextfieldWithNumberBoard";
import ModalAction from "../Modal/ModalAction";
import { useRef } from "react";
import useFocusAndCursorToEnd from "@/shared/hooks/useFocusAndCursorToEndHook";
import Textarea from "../Input/Textarea";
import useCountryConfigHook from "@/shared/hooks/useCountryConfigHook";

const PosOrderCustomDiscountModal = ({ onExit, ...props }: ModalProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const { currency } = useCountryConfigHook();
  const discountOptions = [
    { label: t("percentage"), type: CustomDiscountType.PERCENTAGE },
    {
      label: t("amount", { symbol: currency.symbol }),
      type: CustomDiscountType.FIXED_PRICE,
    },
  ];
  const { register, errors, handleSubmit, control, watch, setValue } =
    useForm();
  const [
    {
      type,
      fixed_price_discount,
      percentage_discount,
      percentage_discount_name,
      fixed_price_discount_name,
    },
    setCustomDiscount,
  ] = usePosOrderCustomDiscount();
  const setModal = useSetModal();
  useFocusAndCursorToEnd(ref);

  const onSubmit = (values) => {
    if (values.type === CustomDiscountType.PERCENTAGE) {
      setCustomDiscount({
        percentage_discount: parseFloat(values.amount),
        percentage_discount_name: values.description,
        type: values.type,
      });
    } else {
      setCustomDiscount({
        fixed_price_discount: parseFloat(values.amount),
        fixed_price_discount_name: values.description,
        type: values.type,
      });
    }
    setModal("", {});
  };

  const typeValue = watch("type") ?? type;

  return (
    <Modal width="40rem" onExit={onExit} {...props}>
      <ModalTitle p="0">{t("posOrders.discount")}</ModalTitle>
      <form
        className="flex flex-col gap-18"
        onSubmit={handleSubmit(onSubmit)}
        style={{ overflow: "hidden" }}
      >
        <Controller
          defaultValue={typeValue || CustomDiscountType.PERCENTAGE}
          name="type"
          control={control}
          render={(props) => (
            <ButtonGroup
              options={discountOptions.map((d) => ({
                id: d.type,
                label: t(d.label),
                onClick: () => props.onChange(d.type),
              }))}
              selected={props.value}
              buttonType="button"
              size="medium"
              label={t("posOrders.discount")}
            />
          )}
        />

        <TextfieldWithNumberBoard
          label={
            typeValue === CustomDiscountType.PERCENTAGE
              ? t("posOrders.percentage")
              : t("posOrders.amount")
          }
          type="number"
          step=".01"
          placeholder={
            typeValue === CustomDiscountType.PERCENTAGE
              ? t("posOrders.percentage")
              : t("posOrders.amount")
          }
          name="amount"
          setValue={(value) => setValue("amount", value)}
          align="right"
          defaultValue={percentage_discount || fixed_price_discount}
          ref={(e) => {
            register(e, {
              required: { value: true, message: t("fieldRequired") },
            });
            ref.current = e;
          }}
          error={errors?.amount?.message}
        />

        <Textarea
          defaultValue={percentage_discount_name || fixed_price_discount_name}
          label={t("posOrders.description")}
          placeholder={t("menu.productDescription")}
          className="w-full"
          rows="3"
          name="description"
          error={errors?.description?.message}
          ref={register({
            required: { value: false, message: t("fieldRequired") },
          })}
        />

        <ModalAction>
          <Button>{t("posOrders.save")}</Button>
        </ModalAction>
      </form>
    </Modal>
  );
};

export default PosOrderCustomDiscountModal;
